import React, { useCallback, useEffect, useState } from "react";
import CollapseBar, { BarTitle } from "../components/CollapseBar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CustomerModal from "../components/CustomerModal";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Copy, Trash2, ArrowRightLeft, Pencil, Star, MessageSquare, RefreshCw, ScanLine } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "firma", label: "FİRMA" },
  { key: "yetkili", label: "YETKİLİ" },
  { key: "gsm", label: "GSM" },
  { key: "telefon", label: "TELEFON" },
  { key: "adres", label: "ADRES" },
  { key: "ilce", label: "İLÇE" },
  { key: "il", label: "İL" },
];

export default function Duplicates() {
  const { columns } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState({});
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [drafts, setDrafts] = useState([]);

  const loadDrafts = useCallback(() => {
    api.get("/scan-drafts", { silent: true }).then((r) => setDrafts(r.data || [])).catch(() => {});
  }, []);
  const acceptDraft = async (id) => {
    try { await api.post(`/scan-drafts/${id}/accept`); toast.success("TASLAK MÜŞTERİ OLARAK EKLENDİ"); loadDrafts(); }
    catch (e) { toast.error(e.response?.data?.detail || "HATA"); }
  };
  const discardDraft = async (id) => {
    try { await api.delete(`/scan-drafts/${id}`); toast.success("TASLAK SİLİNDİ"); loadDrafts(); }
    catch (e) { toast.error("HATA"); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/customers/duplicates", { silent: true }).then((r) => {
      const gs = r.data.groups || [];
      setGroups(gs);
      const t = {}; const p = {};
      gs.forEach((g, i) => { t[i] = g.members[0]?.id; p[i] = new Set(); });
      setTargets(t);
      setPicks(p);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); loadDrafts(); }, [load, loadDrafts]);

  const setTarget = (gi, id) => {
    setTargets((t) => ({ ...t, [gi]: id }));
    setPicks((p) => { const s = new Set(p[gi]); s.delete(id); return { ...p, [gi]: s }; });
  };
  const togglePick = (gi, id) => {
    setPicks((p) => { const s = new Set(p[gi]); s.has(id) ? s.delete(id) : s.add(id); return { ...p, [gi]: s }; });
  };

  const selectedIds = (gi) => [...(picks[gi] || [])].filter((id) => id !== targets[gi]);

  const doTransfer = async (gi, deleteAfter) => {
    const src = selectedIds(gi);
    if (src.length === 0) { toast.error("ÖNCE AKTARILACAK FİRMALARI SEÇİN"); return; }
    setBusy(true);
    try {
      await api.post("/customers/transfer", { target_id: targets[gi], source_ids: src, fill_empty: true, transfer_notes: true, delete_sources: !!deleteAfter });
      toast.success(deleteAfter ? `${src.length} FİRMA ASILA AKTARILDI VE SİLİNDİ` : `${src.length} FİRMA ASILA AKTARILDI`);
      load();
    } catch { toast.error("AKTARMA HATASI"); }
    setBusy(false);
  };

  const doDelete = async () => {
    const { gi } = confirm;
    const src = selectedIds(gi);
    setConfirm(null);
    if (src.length === 0) return;
    setBusy(true);
    try {
      await api.post("/customers/bulk-delete", { data: { ids: src } });
      toast.success(`${src.length} FİRMA SİLİNDİ`);
      load();
    } catch { toast.error("SİLME HATASI"); }
    setBusy(false);
  };

  return (
    <div className="mw-fade-up space-y-5" data-testid="duplicates-page">
      <BarTitle><div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-white flex items-center gap-2">
            <Copy className="h-7 w-7 text-amber-400" /> MÜKERRER FİRMALAR
          </h1>
          <p className="text-sm text-slate-400">
            {loading ? "TARANIYOR..." : `${groups.length} MÜKERRER GRUP · AYNI GSM / TELEFON / ADRES / BENZER FİRMA`}
          </p>
        </div>
        <CollapseBar id="dup-actions" label="İŞLEMLER" className="flex-1 justify-end">
        <Button data-testid="refresh-duplicates" variant="outline" onClick={load} disabled={busy || loading} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">
          <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "mw-spin" : ""}`} /> YENİDEN TARA
        </Button>
        </CollapseBar>
      </div></BarTitle>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
        <Star className="mr-1 inline h-4 w-4" /> NOTU/İŞLEMİ OLAN FİRMA <b>ASIL</b> OLARAK EN ÜSTTE İŞARETLENİR. DİĞERLERİNİ SEÇİP ASILA AKTARABİLİR VEYA SİLEBİLİRSİNİZ.
      </div>

      {drafts.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-indigo-700/40 bg-indigo-950/20" data-testid="scan-drafts-section">
          <div className="flex items-center gap-2 border-b border-indigo-800/40 bg-indigo-900/30 px-4 py-2.5">
            <ScanLine className="h-4 w-4 text-indigo-300" />
            <span className="text-sm font-bold text-white">KARTVİZİT TARAMA TASLAKLARI</span>
            <span className="rounded-full bg-indigo-700/50 px-2 py-0.5 text-xs font-semibold text-indigo-100">{drafts.length}</span>
            <span className="text-xs text-slate-400">Bu kartvizitler sistemdeki bir firmaya benziyor. İsterseniz yine de ekleyin, istemezseniz silin.</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">FİRMA (TARANAN)</th><th className="px-3 py-2">BENZEYEN</th><th className="px-3 py-2">YETKİLİ</th><th className="px-3 py-2">GSM</th><th className="px-3 py-2 text-right">İŞLEM</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.id} data-testid={`draft-${d.id}`} className="border-b border-slate-800/60">
                    <td className="px-3 py-2.5 font-semibold text-slate-200">{d.data?.firma}</td>
                    <td className="px-3 py-2.5 text-amber-300">≈ {d.matched_firma}</td>
                    <td className="px-3 py-2.5 text-slate-300">{d.data?.yetkili || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-300">{d.data?.gsm || "—"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button data-testid={`draft-accept-${d.id}`} size="sm" onClick={() => acceptDraft(d.id)} className="h-8 bg-emerald-600 text-white hover:bg-emerald-700">YİNE DE EKLE</Button>
                        <Button data-testid={`draft-discard-${d.id}`} size="sm" variant="outline" onClick={() => discardDraft(d.id)} className="h-8 border-rose-700 bg-rose-950/40 text-rose-300">SİL</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 px-6 py-16 text-center text-slate-500" data-testid="no-duplicates">
          MÜKERRER FİRMA BULUNAMADI. TEBRİKLER, VERİLERİNİZ TEMİZ.
        </div>
      )}

      <div className="space-y-6">
        {groups.map((g, gi) => (
          <div key={gi} data-testid={`dup-group-${gi}`} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 bg-slate-800/50 px-4 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-white">GRUP {gi + 1}</span>
                <span className="rounded-full bg-slate-700 px-2 py-0.5 text-xs font-semibold text-slate-200">{g.members.length} KAYIT</span>
                {g.reasons.map((r) => (
                  <span key={r} className="rounded-full bg-blue-500/20 px-2 py-0.5 text-xs font-semibold text-blue-300">{r}</span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button data-testid={`transfer-${gi}`} size="sm" disabled={busy} onClick={() => doTransfer(gi, false)} className="bg-emerald-600 text-white hover:bg-emerald-700">
                  <ArrowRightLeft className="mr-1 h-4 w-4" /> SEÇİLİYİ ASILA AKTAR
                </Button>
                <Button data-testid={`transfer-delete-${gi}`} size="sm" disabled={busy} onClick={() => doTransfer(gi, true)} className="bg-indigo-600 text-white hover:bg-indigo-700">
                  <ArrowRightLeft className="mr-1 h-4 w-4" /> AKTAR + SİL
                </Button>
                <Button data-testid={`delete-${gi}`} size="sm" variant="outline" disabled={busy} onClick={() => setConfirm({ gi, n: selectedIds(gi).length })} className="border-rose-700 bg-rose-950/40 text-rose-300 hover:bg-rose-900/40">
                  <Trash2 className="mr-1 h-4 w-4" /> SEÇİLİYİ SİL
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">ASIL</th>
                    <th className="px-3 py-2">SEÇ</th>
                    <th className="px-3 py-2">NOT</th>
                    {FIELDS.map((f) => <th key={f.key} className="px-3 py-2">{f.label}</th>)}
                    <th className="px-3 py-2 text-right">DÜZENLE</th>
                  </tr>
                </thead>
                <tbody>
                  {g.members.map((m) => {
                    const isTarget = targets[gi] === m.id;
                    return (
                      <tr key={m.id} data-testid={`dup-row-${m.id}`} className={`border-b border-slate-800/60 ${isTarget ? "bg-amber-500/10" : "hover:bg-slate-800/40"}`}>
                        <td className="px-3 py-2.5">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input data-testid={`asil-${m.id}`} type="radio" name={`asil-${gi}`} checked={isTarget} onChange={() => setTarget(gi, m.id)} className="h-4 w-4 accent-amber-500" />
                            {isTarget && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
                          </label>
                        </td>
                        <td className="px-3 py-2.5">
                          <Checkbox data-testid={`pick-${m.id}`} disabled={isTarget} checked={(picks[gi] || new Set()).has(m.id)} onCheckedChange={() => togglePick(gi, m.id)} />
                        </td>
                        <td className="px-3 py-2.5">
                          {m.note_count > 0
                            ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-bold text-amber-300"><MessageSquare className="h-3 w-3" />{m.note_count}</span>
                            : <span className="text-slate-600">—</span>}
                        </td>
                        {FIELDS.map((f) => (
                          <td key={f.key} className="px-3 py-2.5 text-slate-200">
                            {m.data?.[f.key] ? String(m.data[f.key]) : <span className="text-slate-600">—</span>}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-right">
                          <button data-testid={`edit-${m.id}`} onClick={() => setEditing(m)} title="DÜZENLE" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-blue-400">
                            <Pencil className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <CustomerModal open={!!editing} onOpenChange={(v) => !v && setEditing(null)} columns={columns} initial={editing} onSaved={() => { setEditing(null); load(); }} />
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title="SEÇİLİ FİRMALAR SİLİNECEK"
        description={`SEÇTİĞİNİZ ${confirm?.n || 0} FİRMA KALICI OLARAK SİLİNECEK. BU İŞLEM GERİ ALINAMAZ.`}
        onConfirm={doDelete}
      />
    </div>
  );
}
